import Polyline from "@arcgis/core/geometry/Polyline"
import buffaloRiverPath from "../data/buffaloRiverPath.json"
import { accessPoints } from "../data/accessPoints"
import { riverPointsOfInterest, POI } from "../data/riverPointsOfInterest"
import { distanceMeters } from "./mathStuff"
import * as geodeticLengthOperator from "@arcgis/core/geometry/operators/geodeticLengthOperator.js";

const METERS_PER_MILE = 1609.34

type Coord = {
    longitude: number
    latitude: number
}

const getNearestPathIndex = (coord: Coord): number => {
    let closestIndex = 0
    let minDist = Number.MAX_SAFE_INTEGER

    buffaloRiverPath.forEach(([lon, lat], index) => {
        const dist = distanceMeters(coord.latitude, coord.longitude, lat, lon)
        if (dist < minDist) {
            minDist = dist
            closestIndex = index
        }
    })

    return closestIndex
}

// Miles from the start of buffaloRiverPath to each vertex, so the distance between
// any two path indices is a subtraction rather than a re-measure of the path.
const cumulativePathMiles: number[] = [0]
for (let i = 1; i < buffaloRiverPath.length; i++) {
    const [prevLon, prevLat] = buffaloRiverPath[i - 1]
    const [lon, lat] = buffaloRiverPath[i]
    cumulativePathMiles.push(cumulativePathMiles[i - 1] + distanceMeters(prevLat, prevLon, lat, lon) / METERS_PER_MILE)
}

const accessPointPathIndices = new Map(
    accessPoints.map(ap => [ap.name, getNearestPathIndex(ap.coord)])
)

export const getAccessPointCoord = (accessPointName: string) => {
    const ap = accessPoints.find(ap => ap.name === accessPointName)
    if (ap !== undefined)
        return ap.coord
    else
        return {
            longitude: 0,
            latitude: 0
        }
}

export const isPrimitiveAccessPoint = (accessPointName: string) => {
    return accessPoints.find(ap => ap.name === accessPointName)?.isPrimitive ?? false
}

export const getAccessPointPathIndex = (accessPointName: string): number => {
    return accessPointPathIndices.get(accessPointName) ?? getNearestPathIndex(getAccessPointCoord(accessPointName))
}

// buffaloRiverPath runs headwaters to mouth, so the lower path index is always the
// upstream point. Returns the pair as [put-in, take-out] so a trip always reads
// with the flow of the river regardless of which point was picked first.
export const orderAccessPointsDownstream = (accessPointName1: string, accessPointName2: string): [string, string] => {
    return getAccessPointPathIndex(accessPointName2) < getAccessPointPathIndex(accessPointName1)
        ? [accessPointName2, accessPointName1]
        : [accessPointName1, accessPointName2]
}

export const getRiverSegmentIndices = (accessPointName1: string, accessPointName2: string): [number, number] => {
    const point1Index = getAccessPointPathIndex(accessPointName1)
    const point2Index = getAccessPointPathIndex(accessPointName2)

    return point2Index < point1Index ? [point2Index, point1Index] : [point1Index, point2Index]
}

export type RiverPointOfInterest = POI & {
    // Names repeat along the river (e.g. three Dry Creeks), so the id includes the path index
    id: string
    pathIndex: number
    riverMile: number
}

export type TripPointOfInterest = RiverPointOfInterest & {
    milesFromPutIn: number
}

const pointsOfInterestAlongRiver: RiverPointOfInterest[] = riverPointsOfInterest
    // Placeholder entries still at 0,0 would snap to an arbitrary point on the path
    .filter(poi => poi.coord.longitude !== 0 || poi.coord.latitude !== 0)
    .map(poi => {
        const pathIndex = getNearestPathIndex(poi.coord)
        return {
            ...poi,
            id: `${poi.name}-${pathIndex}`,
            pathIndex,
            riverMile: cumulativePathMiles[pathIndex]
        }
    })
    .sort((a, b) => a.pathIndex - b.pathIndex)

// Returned in downstream order. Inclusive of both ends so POIs right at an access
// point (e.g. Ponca Creek at Ponca Bridge) are part of the trip.
export const getPointsOfInterestForTrip = (accessPointName1: string, accessPointName2: string): TripPointOfInterest[] => {
    const [startIndex, endIndex] = getRiverSegmentIndices(accessPointName1, accessPointName2)
    const putInMile = cumulativePathMiles[startIndex]

    return pointsOfInterestAlongRiver
        .filter(poi => poi.pathIndex >= startIndex && poi.pathIndex <= endIndex)
        .map(poi => ({ ...poi, milesFromPutIn: poi.riverMile - putInMile }))
}

export const getRiverSegment = (accessPointName1: string, accessPointName2: string): Polyline => {
    const [startIndex, endIndex] = getRiverSegmentIndices(accessPointName1, accessPointName2)

    const segment = new Polyline({
        spatialReference: { wkid: 4326 },
        paths: [buffaloRiverPath.slice(startIndex, endIndex + 1)]
    })

    return segment
}

export const getAverageFloatabilityForSegment = (
    polylineFloatability: number[],
    accessPointName1: string,
    accessPointName2: string
): number | null => {
    const [startIndex, endIndex] = getRiverSegmentIndices(accessPointName1, accessPointName2)
    const scores = polylineFloatability.slice(startIndex, endIndex)

    if (scores.length === 0)
        return null

    return scores.reduce((sum, score) => sum + score, 0) / scores.length
}

export const getDistanceBetweenAccessPoints = async (accessPointName1: string, accessPointName2: string) => {
    const segment = getRiverSegment(accessPointName1, accessPointName2)

    if (!geodeticLengthOperator.isLoaded()) {
        await geodeticLengthOperator.load()
    }

    const distanceMeters = geodeticLengthOperator.execute(
        segment,
        { unit: "meters" }
    )
    const distanceMiles = distanceMeters/METERS_PER_MILE

    return distanceMiles.toFixed(2)
}

const AVERAGE_PADDLING_SPEED_MPH = 2.5

export const estimateTripDurationMinutes = (distanceMiles: number): number => {
    return Math.round((distanceMiles / AVERAGE_PADDLING_SPEED_MPH) * 60)
}

export const formatMinutesLabel = (totalMinutes: number): string => {
    const hours = Math.floor(totalMinutes / 60)
    const minutes = Math.round(totalMinutes % 60)

    if (hours === 0)
        return `${minutes} min`
    if (minutes === 0)
        return `${hours} hr`
    return `${hours} hr ${minutes} min`
}

export const estimateTripDurationLabel = (distanceMiles: number): string => {
    return formatMinutesLabel(estimateTripDurationMinutes(distanceMiles))
}